import React, { Key, useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { useToggle, useNotification, usePaginationQueryParams, useTableSearch, useTableSetting } from '@app/hooks'

import {
  Header,
  Subheader,
  ExtendedTable,
  TableSettingsToolbar,
  SubheaderToolbarDivider,
  PermissionWrapper
} from '@app/components'
import { ContentLayout } from '@app/layouts'
import { Show } from '@app/components/util'
import { CopySchedule } from '@app/modals'
import { LntButton } from '@app/components/base'
import { PERMISSION } from '@app/constants/permission'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORING_SCHEDULE_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { CommonMonitoringScheduleDrawer } from '@app/drawers/CommonMonitoringScheduleDrawer'

import {
  createMonitoringSchedule,
  deleteMonitoringSchedule,
  getMonitoringSchedulesLists,
  editMonitoringSchedule,
  copyMonitoringSchedule
} from '@app/api/routes/monitoringSchedules'

import { TopFilters } from './ui'
import {
  getColumns,
  adaptMonitoringScheduleTableData,
  adaptEditMonitoringSchedule,
  adaptCreateMonitoringSchedule
} from './lib'
import { topFiltersQuerySchema, tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { FiltersParams } from '@app/types/filter'
import type { MonitoringScheduleDataSource, NewMonitoringScheduleData } from './types'
import type { GetMonitoringScheduleParams, CopyMonitoringScheduleData } from '@app/api/routes/monitoringSchedules/types'

const columns = getColumns()

const title = 'Расписание мониторингов'

export const MonitoringSchedulePage: React.FC = () => {
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const [selectedDataRecords, setSelectedDataRecords] = useState<MonitoringScheduleDataSource[]>([])
  const [rowSelectionKeys, setRowSelectionKeys] = useState<number[]>([])

  const notification = useNotification()
  const queryClient = useQueryClient()

  const monitoringScheduleParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetMonitoringScheduleParams

  const { data: monitoringScheduleTableData, isLoading: isMonitoringScheduleDataLoading } = useQuery({
    queryKey: [MONITORING_SCHEDULE_KEY.monitoringScheduleItems, monitoringScheduleParams],
    queryFn: async () => await getMonitoringSchedulesLists(monitoringScheduleParams),
    select: monitoringSchedulesListsData => adaptMonitoringScheduleTableData(monitoringSchedulesListsData)
  })

  const firstMonitoringSchedule = monitoringScheduleTableData?.dataSource?.find(
    data => data.key === rowSelectionKeys[0]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const copyScheduleModalToggle = useToggle()
  const createMonitoringScheduleDrawerToggle = useToggle()
  const editMonitoringScheduleDrawerToggle = useToggle()

  const handleCopySchedule = (data: CopyMonitoringScheduleData): void => {
    const monitoringScheduleId = rowSelectionKeys[0]

    copyScheduleMutation.mutate({ monitoringScheduleId, ...data }, { onSettled: copyScheduleModalToggle.setUntoggled })
  }

  const copyScheduleMutation = useMutation({
    mutationFn: copyMonitoringSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_SCHEDULE_KEY.monitoringScheduleItems] })

      notification.success({
        message: 'Расписание успешно скопировано.'
      })
    }
  })

  const createMonitoringScheduleMutation = useMutation({
    mutationFn: createMonitoringSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_SCHEDULE_KEY.monitoringScheduleItems] })

      notification.success({
        message: 'Успешно:',
        description: 'Расписание на мониторинг успешно создано'
      })
    }
  })

  const editMonitoringScheduleMutation = useMutation({
    mutationFn: editMonitoringSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_SCHEDULE_KEY.monitoringScheduleItems] })

      notification.success({
        message: 'Успешно:',
        description: 'Расписание на мониторинг успешно изменено'
      })
    }
  })

  const deleteMonitoringScheduleMutation = useMutation({
    mutationFn: deleteMonitoringSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_SCHEDULE_KEY.monitoringScheduleItems] })

      notification.success({
        message: 'Расписание удалено',
        description: 'Успешное удаление расписания'
      })
    }
  })

  const handleCreateMonitoringSchedule = (data: NewMonitoringScheduleData): void => {
    const monitoringSchedule = adaptCreateMonitoringSchedule(data)

    createMonitoringScheduleMutation.mutate(monitoringSchedule, {
      onSettled: createMonitoringScheduleDrawerToggle.setUntoggled
    })
  }

  const handleEditMonitoringSchedule = (data: NewMonitoringScheduleData): void => {
    const id = rowSelectionKeys[0]

    const monitoringSchedule = adaptEditMonitoringSchedule(data)

    editMonitoringScheduleMutation.mutate(
      { id, data: monitoringSchedule },
      { onSettled: editMonitoringScheduleDrawerToggle.setUntoggled }
    )
  }

  const handleRemoveMonitoringSchedules = (): void => {
    deleteMonitoringScheduleMutation.mutate({ ids: rowSelectionKeys })
  }

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[], selectedRecords: MonitoringScheduleDataSource[]): void => {
    setRowSelectionKeys(selectedKeys.map(Number))
    setSelectedDataRecords(selectedRecords)
  }

  const subheaderToolbar = (
    <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
      <LntButton
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ createMonitoringScheduleDrawerToggle.setToggled }
      >
        Добавить расписание
      </LntButton>
    </PermissionWrapper>
  )

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.monitoringSchedule, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredMonitoringScheduleDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: monitoringScheduleTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = [
    <Show
      key='date-change-monitoring-schedule-copy'
      condition={ selectedDataRecords.length === 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ copyScheduleModalToggle.setToggled }
        >
          Копировать
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <Show
      key='date-change-monitoring-schedule-autorun-and-delete'
      condition={ selectedDataRecords.length >= 1 }
    >
      { /* TODO: реализовать обработку когда будет готов бэк */ }
      { /* <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
      >
        Включить автозапуск
      </LntButton>

      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
      >
        Отключить автозапуск
      </LntButton> */ }
      <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleRemoveMonitoringSchedules }
        >
          Удалить
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <Show
      key='date-change-monitoring-schedule-edit'
      condition={ selectedDataRecords.length === 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ editMonitoringScheduleDrawerToggle.setToggled }
        >
          Редактировать
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <Subheader toolbar={ subheaderToolbar } />

      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />

        <ExtendedTable
          loading={ isMonitoringScheduleDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredMonitoringScheduleDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: monitoringScheduleTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <CommonMonitoringScheduleDrawer
        title='Создание расписания'
        open={ createMonitoringScheduleDrawerToggle.value }
        onClose={ createMonitoringScheduleDrawerToggle.setUntoggled }
        onSubmit={ handleCreateMonitoringSchedule }
      />

      <CommonMonitoringScheduleDrawer
        title='Редактирование расписания'
        open={ editMonitoringScheduleDrawerToggle.value }
        onClose={ editMonitoringScheduleDrawerToggle.setUntoggled }
        onSubmit={ handleEditMonitoringSchedule }
        data={ firstMonitoringSchedule }
      />

      <CopySchedule
        open={ copyScheduleModalToggle.value }
        onCancel={ copyScheduleModalToggle.setUntoggled }
        onSubmit={ handleCopySchedule }
      />
    </>
  )
}
